import type { SchoolSummary } from '@school-erp/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { api } from '../api/http';
import { t } from '../i18n';

export const errorMessage = (error: unknown): string =>
  error instanceof Error && 'code' in error ? error.message : t('common.actionError');

export function useSchools() {
  return useQuery({ queryKey: ['schools'], queryFn: () => api<SchoolSummary[]>('/schools') });
}

/** Chooses the school to work in; defaults to the first one the user can see. */
export function SchoolSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (schoolId: string) => void;
}) {
  const schools = useSchools();
  const list = schools.data ?? [];
  useEffect(() => {
    if (!value && list[0]) onChange(list[0].id);
  }, [value, list, onChange]);

  if (schools.isPending) return <p role="status">{t('common.loading')}</p>;
  if (list.length === 0) return <p className="text-sm text-slate-600">{t('school.none')}</p>;
  return (
    <div>
      <label htmlFor="school-select" className="block text-sm font-medium">
        {t('school.select')}
      </label>
      <select
        id="school-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded border border-slate-300 bg-white px-3 py-2 text-sm"
      >
        {list.map((school) => (
          <option key={school.id} value={school.id}>
            {school.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Pager({
  page,
  totalPages,
  onPage,
}: {
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
}) {
  return (
    <nav aria-label="Pagination" className="flex items-center justify-between text-sm">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        className="rounded border border-slate-300 px-3 py-1.5 disabled:opacity-50"
      >
        {t('users.prev')}
      </button>
      <span>{t('users.page', { page, total: totalPages })}</span>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
        className="rounded border border-slate-300 px-3 py-1.5 disabled:opacity-50"
      >
        {t('users.next')}
      </button>
    </nav>
  );
}

export function Notice({ kind, children }: { kind: 'ok' | 'error'; children: ReactNode }) {
  return (
    <p
      role={kind === 'error' ? 'alert' : 'status'}
      className={
        kind === 'error'
          ? 'rounded bg-red-50 p-3 text-sm text-red-800'
          : 'rounded bg-green-50 p-3 text-sm text-green-800'
      }
    >
      {children}
    </p>
  );
}

/** Shows validation errors for fields that have no input of their own, so they are never silent. */
export function HiddenFieldErrors({
  errors,
  visible,
}: {
  errors: Record<string, string>;
  visible: string[];
}) {
  const hidden = Object.entries(errors).filter(([field]) => !visible.includes(field));
  if (hidden.length === 0) return null;
  return (
    <Notice kind="error">
      {hidden.map(([field, message]) => `${field}: ${message}`).join('; ')}
    </Notice>
  );
}

export const primaryButton =
  'rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-900 disabled:opacity-50';
export const secondaryButton =
  'rounded border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50';
export const selectClass =
  'mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm';
